import { isEmpty, isPlainObject } from 'lodash';
import { useMemo } from 'react';
import { useOneWayTeacherVideo } from 'store/1wayteachervideo/selectors';
import { useApExams } from 'store/apexams/selectors';
import { useExams } from 'store/exams/selectors';
import { useResources } from 'store/resources/selectors';
import { useStandardExams } from 'store/standardexams';
import { useStory } from 'store/story/selectors';
import { useCurrentUser } from 'store/users/selectors';
import {
  debatePoster,
  GrammarDrillCover,
  SoyHistoriasNew,
} from 'utils/assets/images';
import { isVideo } from 'utils/lib/ebookHelper';

const extractField = (data, key) =>
  data?.[key] ?? data?.data?.[key] ?? data?.assignment?.[key] ?? null;

const getStoryBool = (data) => {
  const storyInstruction = extractField(data, 'storyInstruction');
  return typeof storyInstruction === 'string' && storyInstruction.trim().length > 0;
};

const getTypeValue = (data) =>
  extractField(data, 'type') ??
  extractField(data, 'typeId') ??
  extractField(data, 'assignmentType');

const getWorksheetBool = (data) => getTypeValue(data) === 'worksheet';
const getDebateBool = (data) => getTypeValue(data) === 'debate';

export const getCoverForAssignment = (data) => {
  if (!data) return null;
  if (getStoryBool(data)) return SoyHistoriasNew;
  if (getWorksheetBool(data)) return GrammarDrillCover;
  if (getDebateBool(data)) return debatePoster;
  return null;
};

export const normalizeWorksheetQuestions = (response) => {
  if (!Array.isArray(response)) return [];
  return response
    .filter((element) => element?.question)
    .map((element) => ({
      ...element.question,
      question_id: element.question_id ?? element.question?.id,
      id: element.question_id ?? element.question?.id,
      image: element?.image,
      answer_audio: element?.answer_audio,
      question_audio: element?.question_audio,
    }));
};
const checkGraphic = (act) => {
  const activity_type = act?.data?.activity_type || act?.activity_type;
  if (activity_type === 'graphic_organizer') {
    return true;
  }
  return false;
};
export const isWaitingForAiGradingRestriction = (activeSubmission, activity,isAiPermissionEnabled) => {
  const is_graphic = checkGraphic(activity);
  if (is_graphic || (typeof isAiPermissionEnabled === "boolean" && isAiPermissionEnabled === false)) {
    return false;
  }
  if (
    (activeSubmission &&
      Array.isArray(activeSubmission?.ai_grade) &&
      activeSubmission?.ai_grade.length == 0) ||
    (isPlainObject(activeSubmission?.ai_grade) && isEmpty(activeSubmission?.ai_grade))
  ) {
    const is_waiting = activeSubmission.waiting_for_ai_grade;
    if (is_waiting) {
      return true;
    }
  }
  return false;
};

export const useIsAdditionalBook = (bookId) => {
  const exams = useExams();
  const story = useStory();
  const currentUser = useCurrentUser();
  const onewayTeacherBook = useOneWayTeacherVideo();
  const resources = useResources();
  const apExams = useApExams();
  const standardExams = useStandardExams();
  const apExamBooks = useMemo(() => apExams && Array.isArray(apExams) && apExams.length > 0 ? apExams.flatMap((entry) => entry.exams.map((exam) => Number(exam.bookId))) : [], [apExams]);
  const standardExamBooks = useMemo(() => standardExams && Array.isArray(standardExams) && standardExams.length > 0 ? standardExams.flatMap((entry) => entry.standard_exams.map((exam) => Number(exam.bookId))) : [], [standardExams]);
  const examBooks = useMemo(
    () => (exams && Array.isArray(exams) && exams.length > 0 ? exams.flatMap((entry) => entry.exams.map((exam) => Number(exam.bookId))) : []),
    [exams],
  );
  
  const oneWayStoryId = useMemo(
    () =>
      story
        ?.map((ele) => Number(ele?.bookId))
        .filter((ele) => ele !== null || ele !== undefined || ele !== 0),
    [story],
  );
  const onewayTeacherBookIds = useMemo(() => {
    if (currentUser?.role === 'student') {
      const studentBook = onewayTeacherBook?.filter((book) => book?.for_student) || [];
      return studentBook?.map((book) => book.id) || [];
    }
    return onewayTeacherBook.map((book) => book.id);
  }, [onewayTeacherBook]);
  const oneWayResourcesId = isVideo(bookId, resources);

  const bookMapping = {
    '1nformate': [41],
    comunidad: [43],
    balcónCultural: [33],
    conectandome: [49],
    ap: [70, 69, 71, 72, 73, 75, 78],
    exams: examBooks,
    '1WAYTEACHER': onewayTeacherBookIds,
    '1WAYSTORY': oneWayStoryId,
    '1WAYRESOURCES': oneWayResourcesId ? [Number(bookId)] : [],
    'apExam': apExamBooks,
    'standardExam': standardExamBooks,
  };

  for (const [key, value] of Object.entries(bookMapping)) {
    if (value.map(Number).includes(Number(bookId))) {
      return { key, value: Number(bookId) };
    }
  }
  return null;
};